import { Theme, SxProps } from '@mui/material';
import { PounceStyleProps } from './pounceTypes';
import { typographyTokens } from '../theme/typography-tokens';

// Helper functions for mapping pounce values to actual CSS values
const mapFontSize = (value: string): string => {
  const fontSizes: Record<string, string> = {
    inherit: 'inherit',
    '3x-small': '0.5rem',
    '2x-small': '0.563rem',
    'x-small': '0.625rem',
    small: '0.75rem',
    'small-medium': '0.813rem',
    medium: '0.875rem',
    'medium-large': '0.938rem',
    large: '1rem',
    'x-large': '1.125rem',
    '2x-large': '1.25rem',
    '3x-large': '1.5rem',
    '4x-large': '1.75rem',
    '5x-large': '1.875rem',
    '6x-large': '2.188rem',
    '7x-large': '3.438rem',
  };
  return fontSizes[value] || value;
};

const mapFontWeight = (value: string | number): string | number => {
  if (typeof value === 'number') {
    return value;
  }
  const { fontWeights } = typographyTokens;
  return fontWeights[value as keyof typeof fontWeights] || value;
};

const mapLineHeight = (value: string): string => {
  const lineHeights: Record<string, string> = {
    normal: 'normal',
    none: '1',
    tight: '1.25',
    snug: '1.375',
    typical: '1.5',
    relaxed: '1.625',
    loose: '2',
  };
  return lineHeights[value] || value;
};

const mapBorderRadius = (value: string | number): string | number => {
  if (typeof value === 'number') {
    return value;
  }
  const radii: Record<string, string> = {
    none: '0px',
    small: '2px',
    medium: '4px',
    large: '6px',
    xlarge: '8px',
    pill: '40px',
    circle: '99999px',
  };
  return radii[value] || value;
};

const mapLetterSpacing = (value: string | number): string | number => {
  if (typeof value === 'number') {
    return value;
  }
  const letterSpacings: Record<string, string> = {
    normal: 'normal',
    relaxed: '1.5px',
  };
  return letterSpacings[value] || value;
};

const mapShadow = (value: string): string => {
  const pounceShadows: Record<string, string> = {
    none: 'none',
    dark50: '0px 2px 8px rgba(0, 0, 0, 0.05)',
    dark100: '0px 2px 10px rgba(0, 0, 0, 0.1)',
    dark150: '0px 1px 6px rgba(0, 0, 0, 0.15)',
    dark200: '0px 2px 16px rgba(0, 0, 0, 0.20)',
    dark250: '0px 1px 4px rgba(0, 0, 0, 0.25)',
    dark300: '0px 0px 15px 0px rgba(21, 23, 26, 0.5)',
  };
  return pounceShadows[value] || value;
};

const mapZIndex = (value: string | number): string | number => {
  if (typeof value === 'number') {
    return value;
  }
  const zIndices: Record<string, number> = {
    hide: -1,
    base: 0,
    above: 1,
    dropdown: 1000,
    modal: 1100,
    snackbar: 1200,
    tooltip: 1300,
    max: 9999,
  };
  return zIndices[value] || value;
};

const mapFontFamily = (value: string): string => {
  const fontFamilies: Record<string, string> = {
    primary: 'DM Sans, system-ui, sans-serif',
    mono: '"Roboto Mono", SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  };
  return fontFamilies[value] || value;
};

// Helper function to resolve theme colors
export const resolveThemeColor = (value: string, theme: Theme): string => {
  // Check if it's a theme color reference (e.g., "blue-400")
  if (typeof value === 'string' && value.includes('-')) {
    const paletteValue = theme.palette?.palette?.[value];
    if (paletteValue) {
      return paletteValue;
    }
  }
  return value;
};

// Helper function to add opacity to colors (simplified version)
const addOpacity = (color: string, opacity: number): string => {
  // For hex colors, convert to rgba
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  // For other colors, fallback to CSS color with opacity
  return color; // Could be enhanced to handle more color formats
};

// Pseudo-selector mapping (from pounce pseudo.ts)
const pseudoSelectors: Record<string, string> = {
  _before: '&::before',
  _after: '&::after',
  _hover: '&:hover',
  _active: '&:active, &[data-active=true]',
  _focus: '&:focus',
  _focusWithin: '&:focus-within',
  _visited: '&:visited',
  _empty: '&:empty',
  _even: '&:nth-of-type(even)',
  _odd: '&:nth-of-type(odd)',
  _disabled:
    '&:disabled, &:disabled:focus, &:disabled:hover, &[aria-disabled=true], &[aria-disabled=true]:focus, &[aria-disabled=true]:hover',
  _checked: '&[aria-checked=true]',
  _mixed: '&[aria-checked=mixed]',
  _selected: '&[aria-selected=true], [data-selected] > &',
  _invalid: '&[aria-invalid=true]',
  _pressed: '&[aria-pressed=true]',
  _readOnly: '&[aria-readonly=true], &[readonly]',
  _first: '&:first-of-type',
  _last: '&:last-of-type',
  _expanded: '&[aria-expanded=true]',
  _grabbed: '&[aria-grabbed=true]',
  _notFirst: '&:not(:first-of-type)',
  _notLast: '&:not(:last-of-type)',
  _groupHover: '[role=group]:hover &',
  _autofill: '&:-webkit-autofill',
  _placeholder: '&::placeholder',
};

// Helper function to process pseudo-selector props
const processPseudoProps = (props: any): any => {
  const pseudoStyles: any = {};

  Object.entries(props).forEach(([key, value]) => {
    if (key in pseudoSelectors && value) {
      const selector = pseudoSelectors[key];

      // Process the pseudo-selector value to resolve theme colors
      const processedValue: any = {};
      Object.entries(value).forEach(([cssKey, cssValue]) => {
        // Handle color properties that need theme resolution
        if (
          typeof cssValue === 'string' &&
          (cssKey === 'backgroundColor' ||
            cssKey === 'color' ||
            cssKey === 'borderColor' ||
            cssKey === 'borderTopColor' ||
            cssKey === 'borderRightColor' ||
            cssKey === 'borderBottomColor' ||
            cssKey === 'borderLeftColor' ||
            cssKey === 'fill' ||
            cssKey === 'stroke')
        ) {
          processedValue[cssKey] = (theme: Theme) => resolveThemeColor(cssValue, theme);
        } else {
          processedValue[cssKey] = cssValue;
        }
      });

      pseudoStyles[selector] = processedValue;
    }
  });

  return pseudoStyles;
};

// Main mapper function that converts pounce props to MUI sx
// Only handles pounce-specific props, everything else is passed through since MUI sx already supports styled-system
export const mapPouncePropsToSx = (props: PounceStyleProps): SxProps<Theme> => {
  const sxProps: any = {};
  const pseudoProps: any = {};

  Object.entries(props).forEach(([key, value]) => {
    if (value === undefined) {
      return;
    }

    // Handle pseudo-selector props
    if (key in pseudoSelectors) {
      pseudoProps[key] = value;
      return;
    }

    switch (key) {
      // Pounce-specific aliases that need mapping
      case 'bg':
        sxProps.backgroundColor = (theme: Theme) => resolveThemeColor(value as string, theme);
        break;

      // Space shorthand props
      case 'marginX':
        sxProps.marginLeft = value;
        sxProps.marginRight = value;
        break;
      case 'marginY':
        sxProps.marginTop = value;
        sxProps.marginBottom = value;
        break;
      case 'paddingX':
        sxProps.paddingLeft = value;
        sxProps.paddingRight = value;
        break;
      case 'paddingY':
        sxProps.paddingTop = value;
        sxProps.paddingBottom = value;
        break;

      // Grid spacing props - apply spacing scale
      case 'gridGap':
      case 'gap':
        sxProps.gridGap = (theme: Theme) => theme.spacing(value as number);
        break;
      case 'gridRowGap':
      case 'rowGap':
        sxProps.gridRowGap = (theme: Theme) => theme.spacing(value as number);
        break;
      case 'gridColumnGap':
      case 'columnGap':
        sxProps.gridColumnGap = (theme: Theme) => theme.spacing(value as number);
        break;

      // Typography props - map pounce font sizes to actual values
      case 'fontSize':
        sxProps.fontSize = mapFontSize(value as string);
        break;
      case 'fontWeight':
        sxProps.fontWeight = mapFontWeight(value as string);
        break;
      case 'lineHeight':
        sxProps.lineHeight = mapLineHeight(value as string);
        break;
      case 'letterSpacing':
        sxProps.letterSpacing = mapLetterSpacing(value as string | number);
        break;
      case 'fontFamily':
        sxProps.fontFamily = mapFontFamily(value as string);
        break;
      case 'borderRadius':
        sxProps.borderRadius = mapBorderRadius(value as string | number);
        break;
      case 'borderTopLeftRadius':
        sxProps.borderTopLeftRadius = mapBorderRadius(value as string | number);
        break;
      case 'borderTopRightRadius':
        sxProps.borderTopRightRadius = mapBorderRadius(value as string | number);
        break;
      case 'borderBottomLeftRadius':
        sxProps.borderBottomLeftRadius = mapBorderRadius(value as string | number);
        break;
      case 'borderBottomRightRadius':
        sxProps.borderBottomRightRadius = mapBorderRadius(value as string | number);
        break;

      // Border shorthand props
      case 'borderX':
        sxProps.borderLeft = value;
        sxProps.borderRight = value;
        break;
      case 'borderY':
        sxProps.borderTop = value;
        sxProps.borderBottom = value;
        break;

      case 'shadow':
        sxProps.boxShadow = mapShadow(value as string);
        break;
      case 'boxShadow':
        sxProps.boxShadow = mapShadow(value as string);
        break;

      // Layout props
      case 'zIndex':
        sxProps.zIndex = mapZIndex(value as string | number);
        break;

      // Pounce utility props
      case 'truncated':
        if (value) {
          sxProps.overflow = 'hidden';
          sxProps.textOverflow = 'ellipsis';
          sxProps.whiteSpace = 'nowrap';
        }
        break;

      case 'visuallyHidden':
        if (value) {
          sxProps.border = '0px';
          sxProps.height = '1px';
          sxProps.width = '1px';
          sxProps.margin = '-1px';
          sxProps.padding = '0px';
          sxProps.overflow = 'hidden';
          sxProps.whiteSpace = 'nowrap';
          sxProps.position = 'absolute';
        }
        break;

      case 'backgroundOpacity':
        if (value && props.backgroundColor) {
          // Parse opacity value (0.5, "0.5", 50, "50", "60%")
          let opacity: number;
          if (typeof value === 'string') {
            if (value.includes('%')) {
              opacity = Number(value.replace('%', '')) / 100;
            } else {
              opacity = Number(value);
            }
          } else {
            opacity = value;
          }
          opacity = opacity > 1 ? opacity / 100 : opacity;
          sxProps.backgroundColor = (theme: Theme) => {
            const bgColor = resolveThemeColor(props.backgroundColor as string, theme);
            return addOpacity(bgColor, opacity);
          };
        }
        break;

      case 'borderOpacity':
        if (value && props.borderColor) {
          // Parse opacity value (0.5, "0.5", 50, "50", "60%")
          let opacity: number;
          if (typeof value === 'string') {
            if (value.includes('%')) {
              opacity = Number(value.replace('%', '')) / 100;
            } else {
              opacity = Number(value);
            }
          } else {
            opacity = value;
          }
          opacity = opacity > 1 ? opacity / 100 : opacity;
          sxProps.borderColor = (theme: Theme) => {
            const borderColor = resolveThemeColor(props.borderColor as string, theme);
            return addOpacity(borderColor, opacity);
          };
        }
        break;

      case 'sx':
        // Merge sx prop directly (will be handled later in component)
        break;

      // Color props that need theme resolution for pounce color tokens
      case 'color':
      case 'backgroundColor':
      case 'borderColor':
      case 'borderTopColor':
      case 'borderRightColor':
      case 'borderBottomColor':
      case 'borderLeftColor':
      case 'fill':
      case 'stroke':
        // Skip if opacity variants are being handled
        if (
          (key === 'backgroundColor' && props.backgroundOpacity) ||
          (key === 'borderColor' && props.borderOpacity)
        ) {
          break;
        }
        sxProps[key] = (theme: Theme) => resolveThemeColor(value as string, theme);
        break;

      // All other props are passed directly through - MUI sx already handles styled-system props
      default:
        sxProps[key] = value;
        break;
    }
  });

  // Process pseudo-selector props and merge with main styles
  const processedPseudoStyles = processPseudoProps(pseudoProps);
  const finalSxProps = { ...sxProps, ...processedPseudoStyles };

  return finalSxProps as SxProps<Theme>;
};
